import { Injectable, computed, signal } from '@angular/core';

export type ColorBlindMode = 'off' | 'cividis' | 'viridis';

const VALID_MODES: ReadonlySet<ColorBlindMode> = new Set(['off', 'cividis', 'viridis']);

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  private readonly cbModeSignal = signal<ColorBlindMode>(this.loadCbMode());
  readonly colorBlindMode = this.cbModeSignal.asReadonly();

  /** Convenience: true when any CB-safe palette is active. */
  readonly isColorBlindModeOn = computed(() => this.cbModeSignal() !== 'off');

  private syncFn: ((mode: ColorBlindMode) => void) | null = null;

  constructor() {
    this.applyAttribute(this.cbModeSignal());
    this.listenForStorageChanges();
  }

  registerSync(fn: (mode: ColorBlindMode) => void): void {
    this.syncFn = fn;
  }

  setColorBlindMode(mode: ColorBlindMode): void {
    this.cbModeSignal.set(mode);
    localStorage.setItem('cbMode', mode);
    this.applyAttribute(mode);
    this.syncFn?.(mode);
  }

  /**
   * Hydrate from a backend value. Accepts the new string enum or the legacy
   * boolean shape (true → 'cividis', false → 'off') so older documents still work.
   */
  hydrateFromBackend(value: string | boolean | null | undefined): void {
    const normalized = this.normalize(value);
    if (this.cbModeSignal() === normalized) return;
    this.cbModeSignal.set(normalized);
    localStorage.setItem('cbMode', normalized);
    this.applyAttribute(normalized);
  }

  private loadCbMode(): ColorBlindMode {
    const raw = localStorage.getItem('cbMode');
    // Legacy: 'on' from earlier versions maps to cividis (the original CB ramp).
    if (raw === 'on') return 'cividis';
    return this.normalize(raw);
  }

  private normalize(value: ColorBlindMode | boolean | string | null | undefined): ColorBlindMode {
    if (value === true) return 'cividis';
    if (value === false || value === null || value === undefined) return 'off';
    return VALID_MODES.has(value as ColorBlindMode) ? (value as ColorBlindMode) : 'off';
  }

  private applyAttribute(mode: ColorBlindMode): void {
    const html = document.documentElement;
    if (mode === 'off') {
      html.removeAttribute('data-cb-mode');
    } else {
      html.setAttribute('data-cb-mode', mode);
    }
  }

  // Propagate color-blind mode changes made in another window/WebContentsView
  // of the same origin. The `storage` event only fires in OTHER documents, so
  // the originating window is unaffected.
  private listenForStorageChanges(): void {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== 'cbMode' || e.newValue === null) return;
      const raw = e.newValue;
      const normalized = raw === 'on' ? 'cividis' : this.normalize(raw);
      if (normalized === this.cbModeSignal()) return;
      this.cbModeSignal.set(normalized);
      this.applyAttribute(normalized);
    });
  }
}
